import { FilterBar } from 'core/components/ui/FilterBar'
import { Modal } from 'core/components/ui/Modal'
import { PageHeader } from 'core/components/ui/PageHeader'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GrievanceStatus } from '../../domain/entities/Grievance'
import { AssignAmModal } from '../components/AssignAmModal'
import { GrievanceList } from '../components/GrievanceList'
import { GrievanceRecordWithBuyer, useGrievance } from '../hooks/useGrievance'

export function GrievancesPage() {
  const navigate = useNavigate()
  const {
    records,
    loading,
    error,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    delegateAm,
    resolveIssue,
    resetFilters,
  } = useGrievance(10)

  const [selectedRecord, setSelectedRecord] = useState<GrievanceRecordWithBuyer | null>(null)
  const [assignOpen, setAssignOpen] = useState(false)

  const [resolveOpen, setResolveOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [actioning, setActioning] = useState(false)

  const handleAssignClick = (record: GrievanceRecordWithBuyer) => {
    setSelectedRecord(record)
    setAssignOpen(true)
  }

  const handleResolveClick = (record: GrievanceRecordWithBuyer) => {
    setSelectedRecord(record)
    setNotes('')
    setResolveOpen(true)
  }

  const handleViewClick = (record: GrievanceRecordWithBuyer) => {
    navigate(`/grievances/${record.id}/ledger`)
  }

  const handleAssignSubmit = async (id: string, amId: string, amName: string) => {
    await delegateAm(id, amId, amName)
  }

  const handleResolveConfirm = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedRecord || !notes) return

    setActioning(true)
    try {
      await resolveIssue(selectedRecord.id, notes)
      setResolveOpen(false)
      setSelectedRecord(null)
    } catch (err) {
      // Handled
    } finally {
      setActioning(false)
    }
  }

  const isFilterActive = !!(search || status !== undefined)

  const resolveModalFooter = (
    <>
      <button
        type="button"
        onClick={() => setResolveOpen(false)}
        disabled={actioning}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="resolve-grievance-form"
        disabled={actioning || !notes}
        className="px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors disabled:opacity-50 min-w-[5rem] flex items-center justify-center"
      >
        {actioning ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          'Confirm Resolve'
        )}
      </button>
    </>
  )

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Grievances & Discrepancies"
        description="Address quantity deficits and delivery issues raised by retailers during order reconciliation."
        breadcrumbs={[{ label: 'Portal', path: '/' }, { label: 'Grievances' }]}
      />

      {/* Fetch error banner */}
      {error && (
        <div className="p-3 text-sm bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchPlaceholder="Search order ID or grievance ID..."
        searchValue={search}
        onSearchChange={setSearch}
        showReset={isFilterActive}
        onReset={resetFilters}
      >
        {/* Status Filter */}
        <select
          value={status === undefined ? 'all' : String(status)}
          onChange={(e) => {
            const val = e.target.value
            setStatus(val === 'all' ? undefined : (val as GrievanceStatus))
            setPage(1)
          }}
          className="px-3 py-2 border border-border rounded-btn bg-surface text-text-primary text-xs font-semibold focus:outline-none"
        >
          <option value="open">Open Issues</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved Issues</option>
          <option value="all">All Issues</option>
        </select>
      </FilterBar>

      {/* Data Table list */}
      <GrievanceList
        records={records}
        loading={loading}
        onAssign={handleAssignClick}
        onResolve={handleResolveClick}
        onView={handleViewClick}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
        itemsPerPage={10}
      />

      {/* Assign AM Modal */}
      <AssignAmModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        record={selectedRecord}
      />

      {/* Resolve Grievance Form Modal */}
      <Modal
        isOpen={resolveOpen}
        onClose={() => setResolveOpen(false)}
        title="Resolve Delivery Discrepancy"
        footer={resolveModalFooter}
        size="sm"
      >
        <form id="resolve-grievance-form" onSubmit={handleResolveConfirm} className="space-y-4">
          <div className="space-y-2">
            <p className="text-text-secondary text-sm">
              Please enter resolution details (e.g. refund credit note issued / replacement units scheduled for next
              delivery).
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Refund credit note issued for 15 deficit units..."
              required
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-btn bg-bg text-text-primary text-sm focus:border-primary focus:outline-none transition-colors"
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}
